import { onCleanup } from './cleanup.js'

/**
 * Lazy load de videos e iframes con IntersectionObserver.
 *
 * Video:
 *   <video preload="none" muted playsinline loop data-lazy data-autoplay>
 *     <source data-src="video.mp4" type="video/mp4">
 *   </video>
 *
 * Iframe:
 *   <iframe data-lazy data-src="https://www.youtube.com/embed/..." title="..."></iframe>
 */
function videoIframeLazyLoad () {
  const elements = document.querySelectorAll('video[data-lazy], iframe[data-lazy]')

  if (!elements.length) return

  const loadElement = (el) => {
    if (el.tagName === 'VIDEO') {
      loadVideo(el)
    } else if (el.tagName === 'IFRAME') {
      loadIframe(el)
    }
  }

  if (!('IntersectionObserver' in window)) {
    elements.forEach(loadElement)
    return
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        loadElement(entry.target)
        obs.unobserve(entry.target)
      })
    },
    {
      rootMargin: '200px 0px',
      threshold: 0.01
    }
  )

  elements.forEach((el) => observer.observe(el))
  onCleanup(() => observer.disconnect())
}

/**
 * @param {HTMLVideoElement} video
 */
function loadVideo (video) {
  const sources = video.querySelectorAll('source[data-src]')

  if (sources.length) {
    sources.forEach((source) => {
      source.src = source.dataset.src
      source.removeAttribute('data-src')
    })
  } else if (video.dataset.src) {
    video.src = video.dataset.src
    video.removeAttribute('data-src')
  } else {
    return
  }

  video.load()

  if (video.hasAttribute('data-autoplay') || video.hasAttribute('autoplay')) {
    video.play().catch(() => {})
  }

  video.removeAttribute('data-lazy')
}

/**
 * @param {HTMLIFrameElement} iframe
 */
function loadIframe (iframe) {
  if (!iframe.dataset.src) return

  iframe.src = iframe.dataset.src
  iframe.removeAttribute('data-src')
  iframe.removeAttribute('data-lazy')
}

export default videoIframeLazyLoad
